import { AssessmentSelectDto, SurveyListSelectDto, SurveySelectDto } from '@@types/api.types';
import { DropdownMenuComponent } from '@components/core/dropdown-menu.component';
import { IconButton } from '@components/core/icon-button.component';
import { Score } from '@components/shared/score.component';
import { TextWithLabel } from '@components/shared/text-with-label.component';
import { Tile } from '@components/shared/tile.component';
import { useSurveyNavigation } from '@hooks/surveys.hook';
import { formatAssessmentDate } from '@utils/assessment.util';
import { MRL_TRL_SETTINGS } from '@utils/constants';
import { EllipsisVertical } from 'lucide-react';
import { FC } from 'react';

type MrlScoreTileProps = {
  assessment: AssessmentSelectDto | null;
  canManageAssessments?: boolean;
  /**
   * Draft survey for MRL (if any)
   */
  draftSurvey?: SurveyListSelectDto | null;
  isCreating?: boolean;
  isLoading?: boolean;
  /**
   * Callback to start a new MRL assessment
   */
  onStartNew?: () => void;
  survey: SurveySelectDto | null;
  technologyId: string;
};

const getMrlDescription = (score: number | undefined | null): { description: string; label: string } | null => {
  if (!score || score === 0) return null;

  const levelData = MRL_TRL_SETTINGS.mrl.levels.find((level) => level.level === score);
  return levelData ? { description: levelData.description, label: levelData.label } : null;
};

export const MrlScoreTile: FC<MrlScoreTileProps> = (props) => {
  const {
    assessment,
    canManageAssessments = false,
    draftSurvey,
    isCreating = false,
    isLoading,
    onStartNew,
    survey,
    technologyId,
  } = props;
  const { navigateToSurvey } = useSurveyNavigation();

  const mrlDescription = assessment ? getMrlDescription(assessment.score) : null;
  const questionsCount = survey?.questions?.length ?? 0;
  const hasSurveyLink = Boolean(assessment?.surveyId && technologyId);

  const handleReviewAnswers = () => {
    if (assessment?.surveyId && technologyId) {
      navigateToSurvey(`/technology/${technologyId}/survey/${assessment.surveyId}`);
    }
  };

  const handleResumeDraft = () => {
    if (draftSurvey && technologyId) {
      navigateToSurvey(`/technology/${technologyId}/survey/${draftSurvey.id}?viewFirstUnansweredQuestion=true`);
    }
  };

  // Build menu items based on available actions
  const menuItems = [
    ...(hasSurveyLink ? [{ onClick: handleReviewAnswers, text: 'Review Answers' }] : []),
    ...(canManageAssessments && draftSurvey ? [{ onClick: handleResumeDraft, text: 'Resume Draft' }] : []),
    ...(canManageAssessments && onStartNew
      ? [
          {
            disabled: isCreating,
            onClick: isCreating ? undefined : onStartNew,
            text: isCreating ? 'Starting Assessment...' : 'Start New Assessment',
          },
        ]
      : []),
  ];

  const showMenu = menuItems.length > 0;

  return (
    <Tile
      emptyState={{ title: 'No MRL assessment' }}
      hoverReveal
      isEmpty={!assessment}
      isLoading={isLoading}
    >
      {assessment && (
        <div className="flex flex-col gap-4">
          {/* Score + Actions */}
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-2">
              <Score
                score={assessment.score}
                size="large"
                subtitle="Score"
                title="MRL"
                type="MRL"
              />
            </div>

            {showMenu && (
              <DropdownMenuComponent
                items={menuItems}
                trigger={
                  <IconButton.HoverReveal
                    ariaLabel="Open menu"
                    icon={<EllipsisVertical />}
                    variant="secondary"
                  />
                }
              />
            )}
          </div>

          {/* MRL Description */}
          {mrlDescription && (
            <div className="rounded-lg bg-gray-100 p-3">
              <div className="mb-1 text-sm font-medium text-gray-600">
                MRL {assessment.score} - {mrlDescription.label}
              </div>
              <div className="text-xs leading-[18px] text-gray-500">{mrlDescription.description}</div>
            </div>
          )}

          {/* Assessment Details */}
          <div className="flex flex-col gap-3 rounded-lg bg-gray-100 p-3">
            <div className="grid grid-cols-2 gap-3">
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Assessment Date"
                labelClassName="text-xs"
                title={formatAssessmentDate(assessment)}
              />
              <TextWithLabel
                className="flex-col-reverse text-sm"
                label="Questions"
                labelClassName="text-xs"
                title={questionsCount > 0 ? String(questionsCount) : 'N/A'}
              />
            </div>
          </div>
        </div>
      )}
    </Tile>
  );
};
